import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt, faDollarSign, faCheck, faX, faArrowTrendUp, faLightbulb, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Modal } from './Modal';
import './HelpModal.css';

export const TransfersHelpModal: React.FC<{ isOpen: boolean; onClose: () => void; }> = ({ isOpen, onClose }) => (
    <Modal isOpen={isOpen} onClose={onClose} className="help-modal">
        <h2><FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Transfers Guide</h2>
        <p style={{ fontSize: '1.1em', marginBottom: '24px' }}>Master the transfer market and build your dynasty! 🎯 Smart buying and selling can transform your team from scrappy underdogs into champions. Here's everything you need to know:</p>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faDollarSign} style={{ marginRight: '8px', color: 'var(--info-color)' }} />Transfer Budget</h3>
            <p><strong>How it works:</strong> Your budget is the difference between income and expenses.</p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Income:</strong> Prize money, sponsorships, player sales</li>
                <li><strong>Expenses:</strong> Salaries, training facilities, transfers</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Sell older or surplus players to fund signings</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faArrowTrendUp} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Buying Players</h3>
            <p><strong>The negotiation process:</strong></p>
            <ol style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Find Player:</strong> Browse available players (from other teams)</li>
                <li><strong>Offer Fee:</strong> Make a transfer bid (clubs usually want 120-150% of player value)</li>
                <li><strong>Wages:</strong> Negotiate player salary (factor in weekly cost)</li>
                <li><strong>Finalize:</strong> Deal is complete - player joins your squad</li>
            </ol>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Younger players with high potential are better investments than aging stars</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faCheck} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Selling Players</h3>
            <p><strong>When you sell:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>Get immediate cash (adds to transfer budget)</li>
                <li>Reduces weekly wage bill</li>
                <li>Opens squad slot for new signings (max 30 players)</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Sell on a high - young players with good form command premium fees</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faArrowTrendUp} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Price Strategy</h3>
            <p><strong>Control your selling price:</strong> When selling a player, you can adjust the asking price:</p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>-10% (Quick Sale):</strong> Lower price = 50% better chance to find a buyer quickly</li>
                <li><strong>0% (Standard):</strong> Market value = normal chance to find a buyer</li>
                <li><strong>+10% (Premium):</strong> Higher price = harder to find buyers, but better profit</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>If no buyer is found, the player gets listed for transfer and other clubs may bid weekly</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Listed Players</h3>
            <p><strong>What happens if a player doesn't sell:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>Player is automatically listed for transfer at your asking price</li>
                <li>Each week, other clubs have a 30% chance to express interest</li>
                <li>Player morale decreases when listed (-2 levels)</li>
                <li>Player morale improves if you unlist them (+1 level)</li>
                <li>Listed players can still play in matches and be selected for the XI</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>You can unlist a player anytime to keep them and restore morale</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px', color: 'var(--warning-color)' }} />Squad Management</h3>
            <p><strong>Constraints:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Squad Limit:</strong> Maximum 30 players at any time</li>
                <li><strong>Starting XI:</strong> 11 players on field, rest are reserves/subs</li>
                <li><strong>Positions:</strong> Need variety - GK, DEF, MID, FWD</li>
            </ul>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faArrowTrendUp} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Pro Strategy Tips</h3>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>💰 <strong>The Long Game:</strong> Buy young talent with high potential, develop them, and sell for massive profits!</li>
                <li>📈 <strong>Time Your Sales:</strong> Sell players while they're young and valuable, not when they're aging. Peak value = best profit.</li>
                <li>🎯 <strong>Fill the Gaps:</strong> Identify your weakest positions. A strong defense can win you the league.</li>
                <li>🛡️ <strong>Depth Matters:</strong> Keep 1-2 quality backups for key positions. That defender injury won't hurt if you have cover!</li>
                <li>⏰ <strong>Patience Pays Off:</strong> Don't overpay. Wait for better deals - there's always another player coming to market.</li>
                <li>🚀 <strong>Quick Wins:</strong> Look for unhappy players in bigger clubs - they might be available cheaper!</li>
            </ul>
        </div>

        <div className="modal-actions">
            <button className="button primary" onClick={onClose}>Ready to Trade!</button>
        </div>
    </Modal>
);
